import { Box, Checkbox, Chip, FormControlLabel, IconButton, Tooltip } from "@material-ui/core"
import type { MouseEvent, ReactElement } from "react"

import MyLocation from "@material-ui/icons/MyLocation"
import Visibility from "@material-ui/icons/Visibility"
import { connect } from "react-redux"
import { CardTemplate } from ".."
import type { MapDto } from "../../../models"
import type { AppState, MapSearchResult } from "../../../redux"
import { getMapTitle } from "../../../utils"
import { Caption, Header5 } from "../../atoms"

import "./map-card.styles.scss"
import { useTranslation } from "react-i18next"
import { CardThumbnail } from "../../atoms/card-thumbnail/card-thumbnail.component"

export interface IMapCardOwnProps {
  map: MapSearchResult | MapDto
  big?: boolean | undefined
  selectable?: boolean | undefined
  selected?: boolean
  onClick: (map: MapSearchResult | MapDto) => void
  onPreview?: (map: MapSearchResult | MapDto) => void
}

export interface IMapCardStateProps {
  disableSelect: boolean
}

function MapCardView(props: IMapCardOwnProps & IMapCardStateProps): ReactElement {
  const { map, big, selectable, selected, disableSelect, onClick, onPreview } = props

  const { t } = useTranslation(["general", "myBosatlas", "map"])

  const topographicName: string | undefined =
    "topoName" in map && map.topoName ? `${map.topoName} ${map.topoSuffix ? map.topoSuffix : ""}` : undefined

  function handleOnClick(): void {
    onClick(map)
  }

  function handleCheckboxClick(event: MouseEvent): void {
    event.stopPropagation()
    handleOnClick()
  }

  function handleOnPreview(event: MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation()
    if (onPreview) onPreview(map)
  }

  function CardHeader(): ReactElement {
    return (
      <Box className="map-card-header">
        <Box className="card-actions">
          <FormControlLabel
            label={t("myBosatlas:cards.add")}
            disabled={disableSelect && !selected}
            control={
              <Checkbox
                color="primary"
                disabled={disableSelect && !selected}
                checked={selected}
                onClick={handleCheckboxClick}
              />
            }
          />
        </Box>

        <IconButton className="preview-button" onClick={handleOnPreview} size="small">
          <Visibility />
        </IconButton>
      </Box>
    )
  }

  function CardImage(): ReactElement {
    return (
      <Box className="map-card-media-container">
        <CardLabels />
        <CardThumbnail image={map.thumbnailUrl} />
      </Box>
    )
  }

  function CardLabels(): ReactElement {
    return (
      <Box className="map-card-labels">
        {topographicName && (
          <Tooltip title={topographicName} placement="top-start">
            <Chip
              className="map-card-label topographic"
              label={
                <>
                  <MyLocation />
                  <Caption>{topographicName}</Caption>
                </>
              }
            />
          </Tooltip>
        )}
        {map.isDigitalOnly && <Chip label={t("map:onlyDigital")} className="map-card-label" />}
      </Box>
    )
  }

  return (
    <CardTemplate
      onClick={handleOnClick}
      header={selectable ? <CardHeader /> : undefined}
      image={<CardImage />}
      big={big}
      selected={selected}
      className="map-card"
    >
      <Header5>{getMapTitle(map)}</Header5>
    </CardTemplate>
  )
}

function mapStateToProps(state: AppState) {
  return {
    disableSelect: !state.mapSelector.selectable
  }
}

export const MapCard = connect(mapStateToProps)(MapCardView)
